// Email confirmation — opened from the emailed link (?email=&token=).
//
// Verifies on mount rather than asking for another click: the person already
// clicked, in their inbox, and a second button here is a step that only loses
// people. The outcome is stated plainly, and every failure offers a way
// forward instead of a dead end.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, MailWarning } from 'lucide-react';
import { auth } from '../../lib/api';

type State =
  | { kind: 'working' }
  | { kind: 'done'; already: boolean }
  | { kind: 'failed'; message: string };

export function VerifyEmail() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const token = params.get('token') ?? '';

  const [state, setState] = useState<State>({ kind: 'working' });

  // React 18 StrictMode mounts effects twice in dev. The token is single-use,
  // so a second call would consume it and report failure for a link that
  // actually worked.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    if (!email || !token) {
      setState({ kind: 'failed', message: 'This link is incomplete. Open the link from your email again.' });
      return;
    }

    auth
      .verifyEmail({ email, token })
      .then((r) => setState({ kind: 'done', already: r.already_verified }))
      .catch((e: Error) =>
        setState({ kind: 'failed', message: e.message || 'We could not confirm this link.' }),
      );
  }, [email, token]);

  return (
    <div className="min-h-screen bg-app-bg flex flex-col items-center justify-center px-6 py-12">
      <Link to="/" className="text-[20px] font-extrabold tracking-tight text-brand-600">
        Voye<span className="text-interactive">Lakay</span>
      </Link>

      <div className="mt-5 w-full max-w-md rounded-2xl border border-border bg-white p-6 text-center">
        {state.kind === 'working' && (
          <>
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-brand-500" aria-hidden="true" />
            <p className="mt-3 text-[14px] text-secondary">Confirming your email…</p>
          </>
        )}

        {state.kind === 'done' && (
          <>
            <CheckCircle2 className="mx-auto h-8 w-8 text-success-fg" aria-hidden="true" />
            <h1 className="mt-3 text-[18px] font-semibold text-ink">
              {state.already ? 'Already confirmed' : 'Email confirmed'}
            </h1>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-secondary">
              {state.already
                ? 'This address was already confirmed. You can sign in and carry on.'
                : 'Thank you. You can now send and pay for orders.'}
            </p>
            <Link
              to="/login"
              className="mt-5 inline-flex items-center justify-center rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-brand-700 transition-colors"
            >
              Continue to sign in
            </Link>
          </>
        )}

        {state.kind === 'failed' && (
          <>
            <MailWarning className="mx-auto h-8 w-8 text-warning-fg" aria-hidden="true" />
            <h1 className="mt-3 text-[18px] font-semibold text-ink">We couldn&rsquo;t confirm this link</h1>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-secondary">{state.message}</p>
            {/* The server already says WHY — superseded, expired, or never
                valid — and those need different things from the reader. The
                message above carries it; this only adds the way back. */}
            <p className="mt-3 text-[12.5px] text-secondary">
              Sign in and we can send you a fresh link.
            </p>
            <Link
              to="/login"
              className="mt-5 inline-flex items-center justify-center rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-brand-700 transition-colors"
            >
              Go to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
